import React from 'react';
import { Logo, LogoIcon, NavBarLogo, LogoImage, brand } from '../../brand';
import { colors, surfaces, textColors, typography, borders } from '../../tokens';

/**
 * BrandShowcase
 * Displays brand identity, logo components, and guidelines
 * Theme-aware for dark mode support
 */
const BrandShowcase = () => {
  const CodeBlock = ({ code }) => (
    <pre className="bg-gray-900 dark:bg-dark-950 text-gray-100 dark:text-dark-200 p-4 rounded-lg text-xs overflow-x-auto">
      <code>{code}</code>
    </pre>
  );

  return (
    <div className="space-y-12">
      {/* Brand Identity */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Brand Identity</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Core brand information and identity.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6`}>
            <h3 className={`font-heading text-sm font-bold ${textColors.muted} uppercase mb-3`}>Name</h3>
            <p className={`text-2xl font-bold ${textColors.brightest}`}>{brand.identity.name}</p>
          </div>

          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6`}>
            <h3 className={`font-heading text-sm font-bold ${textColors.muted} uppercase mb-3`}>Domain</h3>
            <p className={`text-2xl font-bold ${textColors.brightest}`}>{brand.identity.domain}</p>
          </div>

          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6 md:col-span-2`}>
            <h3 className={`font-heading text-sm font-bold ${textColors.muted} uppercase mb-3`}>Tagline</h3>
            <p className={`text-xl ${textColors.secondary}`}>{brand.identity.tagline}</p>
          </div>
        </div>
      </section>

      {/* Logo Component */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Logo Component</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Full logo with icon and text. Use for primary branding.
        </p>

        <div className="space-y-6 mb-6">
          {/* Light background */}
          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-8`}>
            <p className={`text-sm ${textColors.muted} mb-4`}>On Light Background</p>
            <Logo iconSize="w-8 h-8" textSize="text-2xl" />
          </div>

          {/* Dark background */}
          <div className="bg-gray-900 dark:bg-dark-950 rounded-lg p-8">
            <p className="text-sm text-gray-400 dark:text-dark-400 mb-4">On Dark Background</p>
            <Logo 
              iconSize="w-8 h-8" 
              textSize="text-2xl"
              iconColor="#60a5fa"
              textColor="text-white"
            />
          </div>

          {/* Different sizes */}
          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-8`}>
            <p className={`text-sm ${textColors.muted} mb-4`}>Size Variations</p>
            <div className="space-y-4">
              <Logo iconSize="w-6 h-6" textSize="text-lg" />
              <Logo iconSize="w-8 h-8" textSize="text-2xl" />
              <Logo iconSize="w-12 h-12" textSize="text-3xl" />
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { Logo } from '../design-system';

<Logo 
  iconSize="w-8 h-8" 
  textSize="text-2xl" 
  iconColor="#60a5fa"
  textColor="text-blue-600"
/>`} />
        </div>
      </section>

      {/* LogoIcon Component */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>LogoIcon Component</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Icon-only logo. Use for compact spaces like mobile navbars or favicons.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6 flex flex-col items-center`}>
            <LogoIcon className="w-12 h-12" color="#60a5fa" />
            <p className={`text-xs ${textColors.muted} mt-2`}>Small</p>
          </div>

          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6 flex flex-col items-center`}>
            <LogoIcon className="w-16 h-16" color="#60a5fa" />
            <p className={`text-xs ${textColors.muted} mt-2`}>Medium</p>
          </div>

          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6 flex flex-col items-center`}>
            <LogoIcon className="w-24 h-24" color="#60a5fa" />
            <p className={`text-xs ${textColors.muted} mt-2`}>Large</p>
          </div>

          <div className="bg-gray-900 dark:bg-dark-950 rounded-lg p-6 flex flex-col items-center">
            <LogoIcon className="w-16 h-16" color="#60a5fa" />
            <p className="text-xs text-gray-400 dark:text-dark-400 mt-2">On Dark</p>
          </div>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { LogoIcon } from '../design-system';

<LogoIcon 
  className="w-12 h-12" 
  color="#60a5fa" 
/>`} />
        </div>
      </section>

      {/* NavBarLogo Component */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>NavBarLogo Component</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Logo optimized for navigation bars with hover animation.
        </p>

        <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-8 mb-6`}>
          <p className={`text-sm ${textColors.muted} mb-4`}>Hover to see animation</p>
          <NavBarLogo iconSize="w-6 h-6" textSize="text-lg" />
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { NavBarLogo } from '../design-system';

<NavBarLogo 
  iconSize="w-6 h-6" 
  textSize="text-lg" 
/>`} />
        </div>
      </section>

      {/* LogoImage Component */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>LogoImage Component</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Image-based logo with different background variants.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6 flex flex-col items-center`}>
            <LogoImage size="60px" variant="noBackground" />
            <p className={`text-xs ${textColors.muted} mt-2`}>No Background</p>
          </div>

          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6 flex flex-col items-center`}>
            <LogoImage size="60px" variant="darkCircle" />
            <p className={`text-xs ${textColors.muted} mt-2`}>Dark Circle</p>
          </div>

          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6 flex flex-col items-center`}>
            <LogoImage size="60px" variant="lightCircle" />
            <p className={`text-xs ${textColors.muted} mt-2`}>Light Circle</p>
          </div>

          <div className="bg-gray-900 dark:bg-dark-950 rounded-lg p-6 flex flex-col items-center">
            <LogoImage size="60px" variant="noBackground" />
            <p className="text-xs text-gray-400 dark:text-dark-400 mt-2">On Dark</p>
          </div>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { LogoImage } from '../design-system';

<LogoImage 
  size="60px" 
  variant="noBackground" 
/>`} />
        </div>
      </section>

      {/* Brand Colors */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Brand Colors</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Official brand color palette - switches from blue (light) to emerald (dark) in landing pages.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6`}>
            <div 
              className="h-24 rounded-lg mb-3" 
              style={{ backgroundColor: brand.colors.primary[600] }}
            />
            <h3 className={`font-heading font-bold ${textColors.primary}`}>Primary Blue</h3>
            <p className={`text-sm ${textColors.muted}`}>{brand.colors.primary[600]}</p>
            <p className={`text-xs ${textColors.tertiary} mt-1`}>For primary CTAs and branding</p>
          </div>

          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6`}>
            <div 
              className="h-24 rounded-lg mb-3" 
              style={{ backgroundColor: brand.colors.accent[400] }}
            />
            <h3 className={`font-heading font-bold ${textColors.primary}`}>Accent Green</h3>
            <p className={`text-sm ${textColors.muted}`}>{brand.colors.accent[400]}</p>
            <p className={`text-xs ${textColors.tertiary} mt-1`}>For success and positive actions</p>
          </div>

          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6`}>
            <div 
              className="h-24 rounded-lg mb-3" 
              style={{ background: brand.colors.gradients.primary.css }}
            />
            <h3 className={`font-heading font-bold ${textColors.primary}`}>Primary Gradient</h3>
            <p className={`text-xs ${textColors.tertiary} mt-1`}>For hero sections and key moments</p>
          </div>
        </div>
      </section>

      {/* Logo Guidelines */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Logo Guidelines</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Best practices for using the MyVitae logo.
        </p>

        <div className="space-y-6">
          {/* Minimum Sizes */}
          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6`}>
            <h3 className={`font-heading font-bold ${textColors.primary} mb-3`}>Minimum Sizes</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className={`text-sm ${textColors.secondary} mb-2`}>Horizontal Logo</p>
                <div className={`${surfaces.secondary} p-4 rounded`}>
                  <Logo iconSize="w-8 h-8" textSize="text-xl" />
                </div>
                <p className={`text-xs ${textColors.muted} mt-2`}>
                  Minimum: {brand.guidelines.logo.minSizes.horizontal.width} × {brand.guidelines.logo.minSizes.horizontal.height}
                </p>
              </div>
              <div>
                <p className={`text-sm ${textColors.secondary} mb-2`}>Icon Only</p>
                <div className={`${surfaces.secondary} p-4 rounded flex justify-center`}>
                  <LogoIcon className="w-8 h-8" color="#60a5fa" />
                </div>
                <p className={`text-xs ${textColors.muted} mt-2`}>
                  Minimum: {brand.guidelines.logo.minSizes.icon.width} × {brand.guidelines.logo.minSizes.icon.height}
                </p>
              </div>
            </div>
          </div>

          {/* Clear Space */}
          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6`}>
            <h3 className={`font-heading font-bold ${textColors.primary} mb-3`}>Clear Space</h3>
            <p className={`text-sm ${textColors.secondary} mb-4`}>
              {brand.guidelines.logo.clearSpace}
            </p>
            <div className={`${surfaces.secondary} p-8 rounded inline-block`}>
              <div className="border-2 border-dashed border-blue-300 dark:border-blue-700 p-8">
                <Logo iconSize="w-8 h-8" textSize="text-xl" />
              </div>
            </div>
          </div>

          {/* Do's and Don'ts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
              <h3 className="font-heading font-bold text-green-900 dark:text-green-200 mb-3">✓ Do</h3>
              <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
                <li>• Use on clean, uncluttered backgrounds</li>
                <li>• Maintain proper aspect ratio</li>
                <li>• Use approved color variations</li>
                <li>• Ensure sufficient contrast</li>
                <li>• Respect minimum size requirements</li>
              </ul>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
              <h3 className="font-heading font-bold text-red-900 dark:text-red-200 mb-3">✗ Don't</h3>
              <ul className="space-y-2 text-sm text-red-800 dark:text-red-300">
                <li>• Stretch or distort the logo</li>
                <li>• Rotate the logo</li>
                <li>• Use unapproved colors</li>
                <li>• Place on busy backgrounds</li>
                <li>• Add effects (shadows, outlines)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Color Usage Guidelines */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Color Usage Guidelines</h2>
        
        <div className="space-y-6">
          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6`}>
            <h3 className={`font-heading font-bold ${textColors.primary} mb-3`}>Primary Blue</h3>
            <div className="flex items-center gap-4 mb-3">
              <div 
                className="w-16 h-16 rounded" 
                style={{ backgroundColor: colors.primary[600] }}
              />
              <div className="flex-1">
                <p className={`text-sm ${textColors.primary} font-medium mb-1`}>Use for:</p>
                <p className={`text-sm ${textColors.secondary}`}>Primary buttons, headers, navigation bars, links</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded border-2 ${borders.default} flex items-center justify-center text-2xl`}>
                ❌
              </div>
              <div className="flex-1">
                <p className={`text-sm ${textColors.primary} font-medium mb-1`}>Avoid for:</p>
                <p className={`text-sm ${textColors.secondary}`}>Large background areas, body text, error states</p>
              </div>
            </div>
          </div>

          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6`}>
            <h3 className={`font-heading font-bold ${textColors.primary} mb-3`}>Accent Green</h3>
            <div className="flex items-center gap-4 mb-3">
              <div 
                className="w-16 h-16 rounded" 
                style={{ backgroundColor: colors.accent[400] }}
              />
              <div className="flex-1">
                <p className={`text-sm ${textColors.primary} font-medium mb-1`}>Use for:</p>
                <p className={`text-sm ${textColors.secondary}`}>Success messages, "Create" buttons, completion states, growth indicators</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded border-2 ${borders.default} flex items-center justify-center text-2xl`}>
                ❌
              </div>
              <div className="flex-1">
                <p className={`text-sm ${textColors.primary} font-medium mb-1`}>Avoid for:</p>
                <p className={`text-sm ${textColors.secondary}`}>Primary navigation, neutral messages, destructive actions</p>
              </div>
            </div>
          </div>

          <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6`}>
            <h3 className={`font-heading font-bold ${textColors.primary} mb-3`}>Gradients</h3>
            <div className="flex items-center gap-4 mb-3">
              <div 
                className="w-16 h-16 rounded" 
                style={{ background: colors.gradients.primary.css }}
              />
              <div className="flex-1">
                <p className={`text-sm ${textColors.primary} font-medium mb-1`}>Use for:</p>
                <p className={`text-sm ${textColors.secondary}`}>Landing page hero, navigation bars, auth pages, key brand moments</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded border-2 ${borders.default} flex items-center justify-center text-2xl`}>
                ❌
              </div>
              <div className="flex-1">
                <p className={`text-sm ${textColors.primary} font-medium mb-1`}>Avoid for:</p>
                <p className={`text-sm ${textColors.secondary}`}>Small UI components, text, overuse throughout app</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Assets */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Brand Assets</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Available logo files and assets.
        </p>

        <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6`}>
          <div className="space-y-3">
            <div className={`flex items-center justify-between py-2 border-b ${borders.light}`}>
              <span className={`text-sm ${textColors.secondary}`}>No Background</span>
              <code className={`text-xs ${textColors.muted}`}>{brand.assets.logos.noBackground}</code>
            </div>
            <div className={`flex items-center justify-between py-2 border-b ${borders.light}`}>
              <span className={`text-sm ${textColors.secondary}`}>Dark Circle</span>
              <code className={`text-xs ${textColors.muted}`}>{brand.assets.logos.darkCircle}</code>
            </div>
            <div className={`flex items-center justify-between py-2 border-b ${borders.light}`}>
              <span className={`text-sm ${textColors.secondary}`}>Light Circle</span>
              <code className={`text-xs ${textColors.muted}`}>{brand.assets.logos.lightCircle}</code>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className={`text-sm ${textColors.secondary}`}>Favicon</span>
              <code className={`text-xs ${textColors.muted}`}>{brand.assets.favicon}</code>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrandShowcase;
